import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { Spinner, MessageBar, MessageBarBody } from "@fluentui/react-components";
import { LeaveSystemConfig, type DataMode } from "../config/leaveSystemConfig";
import {
  CurrentUser,
  Employee,
  Holiday,
  LeaveType,
} from "../models/leaveModels";
import { createLeaveService, type LeaveDataService } from "../services/leaveService";
import { isApproverDepartment } from "../config/leavePolicy";
import { useGraph } from "../hooks/useGraph";
import { Login } from "../components/Login";

interface AppContextValue {
  service: LeaveDataService;
  currentUser: CurrentUser;
  employee: Employee | null;
  /**
   * True for Admin / Management department (see APPROVER_DEPARTMENTS): they
   * get the Manager Queue. Technical / Functional employees don't.
   */
  isManager: boolean;
  leaveTypes: LeaveType[];
  holidays: Holiday[];
  year: number;
  dataMode: DataMode;
  /** Bumped whenever data changes so screens can refetch. */
  refreshSignal: number;
  triggerRefresh: () => void;
  /** Mock-only: seed users you can view the demo as (empty in graph mode). */
  switchableUsers: CurrentUser[];
  /** Mock-only: switch the acting user. */
  switchUser: (email: string) => void;
  /** Sign the current user out (graph mode). No-op in mock mode. */
  signOut: () => void;
  /** Current user's Entra profile photo (object URL), or null → show initials. */
  photoUrl: string | null;
}

const AppContext = createContext<AppContextValue | null>(null);

export function useApp(): AppContextValue {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used within <AppProvider>");
  return ctx;
}

export function AppProvider({ children }: { children: ReactNode }) {
  const { graph, error: graphError, ready, needsSignIn, signIn, signOut } = useGraph();
  const year = new Date().getFullYear();

  const [state, setState] = useState<{
    currentUser: CurrentUser;
    employee: Employee | null;
    leaveTypes: LeaveType[];
    holidays: Holiday[];
  } | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [refreshSignal, setRefreshSignal] = useState(0);
  const [switchableUsers, setSwitchableUsers] = useState<CurrentUser[]>([]);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [userVersion, setUserVersion] = useState(0);

  const service = useMemo<LeaveDataService | null>(() => {
    if (!ready || needsSignIn) return null;
    return createLeaveService(graph);
  }, [graph, ready, needsSignIn]);

  const triggerRefresh = useCallback(() => setRefreshSignal((n) => n + 1), []);

  const switchUser = useCallback(
    (email: string) => {
      service?.setActingUser?.(email);
      setUserVersion((v) => v + 1);
      setRefreshSignal((n) => n + 1);
    },
    [service],
  );

  useEffect(() => {
    if (!service) return;
    let cancelled = false;
    (async () => {
      try {
        const currentUser = await service.getCurrentUser();
        // Next year's holidays too, so the date picker is right across New Year.
        const [employee, leaveTypes, thisYear, nextYear, users] = await Promise.all([
          service.getEmployeeByEmail(currentUser.email),
          service.getLeaveTypes(),
          service.getHolidays(year),
          service.getHolidays(year + 1),
          service.getSwitchableUsers?.() ?? Promise.resolve([]),
        ]);
        if (cancelled) return;
        setState({ currentUser, employee, leaveTypes, holidays: [...thisYear, ...nextYear] });
        setSwitchableUsers(users);
        service.getMyPhotoUrl?.().then((url) => { if (!cancelled) setPhotoUrl(url ?? null); }).catch(() => {});
      } catch (err) {
        if (cancelled) return;
        // Graph returns 403 "Access denied" when the signed-in person has no
        // permission on the SharePoint site — the usual first-login problem.
        const status = (err as { statusCode?: number }).statusCode;
        setLoadError(
          status === 403
            ? "your account doesn't have access to the KTI Leave System SharePoint site yet (Access denied). Ask the site owner to add you, then refresh this page."
            : String(err),
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [service, year, userVersion]);

  if (graphError) {
    return (
      <CenteredMessage intent="error">
        Sign-in / Graph error: {graphError}
      </CenteredMessage>
    );
  }
  if (needsSignIn) {
    return <Login onSignIn={signIn} />;
  }
  if (loadError) {
    return (
      <CenteredMessage intent="error">
        Failed to load leave data: {loadError}
      </CenteredMessage>
    );
  }
  if (!service || !state) {
    return (
      <div style={{ display: "grid", placeItems: "center", height: "60vh" }}>
        <Spinner label="Loading KTI Leave Management…" />
      </div>
    );
  }

  const value: AppContextValue = {
    service,
    currentUser: state.currentUser,
    employee: state.employee,
    isManager: isApproverDepartment(state.employee?.department),
    leaveTypes: state.leaveTypes,
    holidays: state.holidays,
    year,
    dataMode: LeaveSystemConfig.dataMode,
    refreshSignal,
    triggerRefresh,
    switchableUsers,
    switchUser,
    signOut,
    photoUrl,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

function CenteredMessage({
  intent,
  children,
}: {
  intent: "error" | "info";
  children: ReactNode;
}) {
  return (
    <div style={{ maxWidth: 520, margin: "48px auto", padding: "0 16px" }}>
      <MessageBar intent={intent}>
        <MessageBarBody>{children}</MessageBarBody>
      </MessageBar>
    </div>
  );
}
